// Per-message delivery ack values, per WAWeb's Ack model.
//
//   -1 error    | 0 pending  | 1 server  | 2 device  | 3 read  | 4 played
//
// Engine emits `null` when it couldn't determine the ack. Batch shape
// is the per-key entry returned by waGetMessagesAckDataTool.

import { z } from 'zod';
import { WireKeySchema } from '../../contract/wire-identity';

/** wa-js Message.ack ladder — the single source for ack magic numbers. */
export const ACK = { ERROR: -1, PENDING: 0, SERVER: 1, DEVICE: 2, READ: 3, PLAYED: 4 } as const;
export type AckLevel = typeof ACK[keyof typeof ACK];

/** The public `ackLabel` of each ack level (webhook / event payloads). An ack
 *  outside the ladder is labelled `unknown_<ack>` by the emitter. */
export const ACK_LABELS: Readonly<Record<AckLevel, string>> = {
  [ACK.ERROR]: 'error',
  [ACK.PENDING]: 'pending',
  [ACK.SERVER]: 'sent',
  [ACK.DEVICE]: 'delivered',
  [ACK.READ]: 'read',
  [ACK.PLAYED]: 'played',
};

export const BtSlimMessageAckSchema = z.object({
  ack: z.number().int().nullable(),
  error: z.string().nullable().optional(),
});
export type BtSlimMessageAck = z.infer<typeof BtSlimMessageAckSchema>;

/**
 * Entry per requested messageKey in waGetMessagesAckDataTool's batch
 * response. `key` echoes the input identifier; `ack` matches the
 * MessageAck enum above.
 */
export const BtSlimBatchMessageAckEntrySchema = z.object({
  key: WireKeySchema,
  ack: z.number().int().nullable(),
  // Per-recipient receipt COUNTS from WAWeb's MsgInfo (queryMsgInfo), projected by the
  // engine's batch handler. GROUP-send semantics only: the scalar ack advances 1→2 only
  // when EVERY participant received, so big groups pin at ack=1 despite near-full
  // delivery — these counts let the backend reconciler distinguish "delivered with an
  // offline tail" (deliveredCount>0 → promote) from a real sender-key fan-out failure
  // (0 receipts after the grace age → classify, don't promote; BE#166).
  // ALL optional: engines older than the projection simply omit them (scalar-ack fallback).
  deliveredCount: z.number().int().nonnegative().optional(),
  readCount: z.number().int().nonnegative().optional(),
  playedCount: z.number().int().nonnegative().optional(),
  /** Participants whose delivery receipt is still outstanding (MsgInfo.deliveryRemaining). */
  deliveryRemaining: z.number().int().nonnegative().optional(),
});
export type BtSlimBatchMessageAckEntry = z.infer<typeof BtSlimBatchMessageAckEntrySchema>;
